import { updateDcoin } from '../functions/updateDcoin';

export interface DailyRewardResult {
    rewarded: boolean;
    message: string;
    newBalance?: number;
}

export interface DailyRewardCheckResult {
    eligible: boolean;
    message: string;
}

const DAILY_REWARD_KEY = 'daily_reward_last_claim_date';
const DAILY_REWARD_AMOUNT = 100;

/**
 * 检查是否可以领取每日奖励（使用本地存储）
 * @returns 检查结果
 */
export const checkDailyRewardEligibility = (): DailyRewardCheckResult => {
    try {
        const today = new Date();
        const todayDateString = today.toISOString().split('T')[0]; // YYYY-MM-DD

        const lastClaimDate = localStorage.getItem(DAILY_REWARD_KEY);

        if (lastClaimDate === todayDateString) {
            return {
                eligible: false,
                message: 'Already claimed today'
            };
        }

        return {
            eligible: true,
            message: 'Eligible for daily reward'
        };
    } catch (error) {
        console.error('Error checking daily reward:', error);
        return { eligible: false, message: 'Check failed' };
    }
};

/**
 * 标记每日奖励已领取
 */
export const markDailyRewardAsClaimed = () => {
    try {
        const today = new Date();
        const todayDateString = today.toISOString().split('T')[0];
        localStorage.setItem(DAILY_REWARD_KEY, todayDateString);
    } catch (error) {
        console.error('Error marking daily reward:', error);
    }
};

/**
 * 领取每日奖励
 * @param token JWT Token
 * @param username 用户名
 * @returns 奖励结果
 */
export const claimDailyReward = async (
    token: string,
    username: string
): Promise<DailyRewardResult> => {
    try {
        // 发放奖励
        const result = await updateDcoin(token, username, DAILY_REWARD_AMOUNT);

        // Mark as claimed locally
        markDailyRewardAsClaimed();

        // 获取新余额
        let newBalance: number | undefined;
        if (result && typeof result.coin === 'number') {
            newBalance = result.coin;
        } else if (result && result.user && typeof result.user.coin === 'number') {
            newBalance = result.user.coin;
        } else if (result && result.data && typeof result.data.coin === 'number') {
            newBalance = result.data.coin;
        }

        return {
            rewarded: true,
            message: `You received ${DAILY_REWARD_AMOUNT} DCOIN!`,
            newBalance
        };
    } catch (error) {
        console.error('Failed to claim daily reward:', error);
        return {
            rewarded: false,
            message: 'Failed to claim daily reward'
        };
    }
};

/**
 * 检查并领取每日登录奖励
 * @param token JWT Token
 * @param username 用户名
 * @returns 奖励结果
 */
export const checkAndClaimDailyReward = async (
    token: string,
    username: string
): Promise<DailyRewardResult> => {
    const eligibility = checkDailyRewardEligibility();

    if (!eligibility.eligible) {
        return {
            rewarded: false,
            message: eligibility.message
        };
    }

    return await claimDailyReward(token, username);
};

export const REWARD_AMOUNT = DAILY_REWARD_AMOUNT;
